import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Heart,
  MessageCircle,
  Share2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { CommentSection } from '@/components/CommentSection'
import { PostMenu } from '@/components/PostMenu'
import { FeedItem, User } from '@/lib/api'
import { formatRelativeTime, getInitials, formatNumber } from '@/lib/utils'

interface PostCardProps {
  item: FeedItem
  onReact: (postId: string, isLiked: boolean) => void
  currentUser?: User | null
  className?: string
  onDelete?: (postId: string) => void
  onEdit?: (postId: string, content: string, mediaIds: string[]) => void
}

export function PostCard({
  item,
  onReact,
  currentUser,
  className = '',
  onDelete,
  onEdit,
}: PostCardProps) {
  const [showComments, setShowComments] = useState(false)
  const post = item.post.post
  const author = item.post.author
  const media = item.post.media || []
  const isLiked = item.post.liked_by_me
  const likeCount = item.post.like_count || 0

  // Use author info if available, otherwise show current user (for own posts)
  const displayName = author?.displayName || author?.display_name || currentUser?.displayName || 'User'
  const username = author?.username || currentUser?.username || 'user'
  const avatarUrl = author?.avatarUrl || currentUser?.avatarUrl

  // Check if this is the current user's post
  const isOwnPost = currentUser?.id === post.author_id

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="flex items-center gap-3">
          <Avatar>
            <AvatarImage src={avatarUrl} />
            <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium">{displayName}</p>
            <p className="text-xs text-muted-foreground">
              @{username} · {formatRelativeTime(post.created_at)}
            </p>
          </div>
        </div>
        <PostMenu
          item={item}
          isOwnPost={isOwnPost}
          onDelete={onDelete}
          onEdit={onEdit}
        />
      </CardHeader>

      <CardContent className="pb-3">
        {post.content && <p className="whitespace-pre-wrap">{post.content}</p>}

        {/* Media Gallery */}
        {media.length > 0 && (
          <div className={`${post.content ? 'mt-3' : ''} overflow-hidden rounded-lg`}>
            {media.length === 1 ? (
              // Single image - full width
              <img
                src={media[0].url}
                alt=""
                className="w-full object-cover"
                style={{ maxHeight: 500 }}
              />
            ) : media.length === 2 ? (
              // Two images - side by side
              <div className="grid grid-cols-2 gap-1">
                {media.map((m) => (
                  <div key={m.id} className="relative aspect-square">
                    <img
                      src={m.url}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  </div>
                ))}
              </div>
            ) : media.length === 3 ? (
              // Three images - one large, two small
              <div className="grid grid-cols-2 gap-1">
                <div className="row-span-2">
                  <img
                    src={media[0].url}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                </div>
                {media.slice(1, 3).map((m) => (
                  <div key={m.id} className="relative aspect-square">
                    <img
                      src={m.url}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  </div>
                ))}
              </div>
            ) : (
              // Four or more images - 2x2 grid with overflow indicator
              <div className="grid grid-cols-2 gap-1">
                {media.slice(0, 4).map((m, index) => (
                  <div key={m.id} className="relative aspect-square">
                    <img
                      src={m.url}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                    {index === 3 && media.length > 4 && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-2xl font-bold text-white">
                        +{media.length - 4}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>

      <CardFooter className="flex-col items-stretch border-t pt-3">
        <div className="flex w-full items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            className={isLiked ? 'text-red-500 hover:text-red-600' : 'text-muted-foreground'}
            onClick={() => onReact(post.id, isLiked)}
          >
            <Heart className={`mr-1 h-4 w-4 ${isLiked ? 'fill-current' : ''}`} />
            {likeCount > 0 && formatNumber(likeCount)}
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            onClick={() => setShowComments(!showComments)}
          >
            <MessageCircle className="mr-1 h-4 w-4" />
            {post.comment_count > 0 && formatNumber(post.comment_count)}
          </Button>

          <Button variant="ghost" size="sm" className="text-muted-foreground">
            <Share2 className="mr-1 h-4 w-4" />
            {post.share_count > 0 && formatNumber(post.share_count)}
          </Button>
        </div>

        {/* Comment Section */}
        <CommentSection
          postId={post.id}
          currentUser={currentUser}
          isExpanded={showComments}
        />
      </CardFooter>
    </Card>
  )
}
